import { z } from '@customerio/hydra-validation';
import type { AddonSku } from '@/services/billing/addon-change';
import { BILLING_TIME_ZONE } from '@/services/billing/time-zone';
import { formatCentsToUsd } from '@/utils/currency';
import { ACTIVITY_HISTORY_WINDOWS, type ActivityHistoryKind } from './activity-history';

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: BILLING_TIME_ZONE });

export function formatShortDate(unixSeconds: number): string {
  return SHORT_DATE.format(new Date(unixSeconds * 1000));
}

export type AddonCadence = 'monthly' | 'one-time';

export type AddonKey = 'segments' | 'activity-history' | 'flex-recharge' | 'flex-recharge-auto' | 'generic';

export type AddonRequest =
  | { key: 'segments'; includedCount: number }
  /** Unix seconds. */
  | { key: 'activity-history'; kind: ActivityHistoryKind; lookbackStart: number; addonLookbackStart: number }
  | { key: 'flex-recharge' | 'flex-recharge-auto'; usedPercent: number }
  | { key: 'generic' };

/** Checks a request that crosses from an embedded app, where the `AddonRequest` type can't follow. */
export const addonRequestSchema = z.union([
  z.object({ key: z.literal('segments'), includedCount: z.number().int().positive() }),
  z.object({
    key: z.literal('activity-history'),
    kind: z.enum(['message', 'profile']),
    lookbackStart: z.number(),
    addonLookbackStart: z.number(),
  }),
  z.object({ key: z.enum(['flex-recharge', 'flex-recharge-auto']), usedPercent: z.number() }),
  z.object({ key: z.literal('generic') }),
]) satisfies z.ZodType<AddonRequest>;

export type AfterLineRun = { text: string; isStrong?: boolean } | 'arrow';

export type AddonConfig = {
  title: string;
  offerName: string;
  cadence: AddonCadence;
  priceCents: number;
  goodToKnow: readonly string[];
  hasAutoRecharge: boolean;
};

export type AddonOffer = AddonConfig & { afterLine: readonly AfterLineRun[] };

/** Dates are unix seconds. */
export type AddonProration = {
  today: number;
  nextBillingDate: number;
  daysLeft: number;
  daysInCycle: number;
  proratedCents: number;
};

const MONTHLY_BILLING = ['Prorated now, then billed on the 1st each month', 'Turn it off any time from Billing'];

export const FLEX_RECHARGE_FULL_CENTS = 1000;
/** Placeholder floor from the prototype: Stripe's fixed fee makes tiny refills uneconomical. */
export const FLEX_RECHARGE_MIN_CENTS = 50;

/** Usage can run past 100% while limits aren't enforced, but a recharge never costs more than a full one. */
export function clampRechargePercent(usedPercent: number): number {
  return Math.min(usedPercent, 100);
}

function proratedRechargeCents(usedPercent: number): number {
  return Math.round((FLEX_RECHARGE_FULL_CENTS * clampRechargePercent(usedPercent)) / 100);
}

/** A recharge costs the share of the gauge used, so it always takes usage back to 0%. */
export function flexRechargeCents(usedPercent: number): number {
  return Math.max(FLEX_RECHARGE_MIN_CENTS, proratedRechargeCents(usedPercent));
}

export function isFlexRechargeAtMinimum(usedPercent: number): boolean {
  return proratedRechargeCents(usedPercent) < FLEX_RECHARGE_MIN_CENTS;
}

const FLEX_RECHARGE: AddonConfig = {
  title: 'Flex recharge',
  offerName: 'Flex recharge',
  cadence: 'one-time',
  priceCents: FLEX_RECHARGE_FULL_CENTS,
  goodToKnow: [
    'Billed once to your card, never from your credit balance',
    "Doesn't renew, so recharge whenever you need to",
  ],
  hasAutoRecharge: false,
};

export const ADDONS = {
  segments: {
    title: 'Unlimited data-driven segments',
    offerName: 'Segments add-on',
    cadence: 'monthly',
    priceCents: 1000,
    goodToKnow: ['Manual segments stay unlimited', ...MONTHLY_BILLING],
    hasAutoRecharge: false,
  },
  'activity-history': {
    title: '30 days of activity history',
    offerName: 'Data retention add-on',
    cadence: 'monthly',
    priceCents: 1000,
    goodToKnow: ['Covers message activity and profile activity, lookback and export range', ...MONTHLY_BILLING],
    hasAutoRecharge: false,
  },
  'flex-recharge': FLEX_RECHARGE,
  'flex-recharge-auto': { ...FLEX_RECHARGE, hasAutoRecharge: true },
  generic: {
    title: 'Add-on name',
    offerName: 'Add-on',
    cadence: 'monthly',
    priceCents: 1000,
    goodToKnow: ['What stays included for free', ...MONTHLY_BILLING],
    hasAutoRecharge: false,
  },
} satisfies Record<AddonKey, AddonConfig>;

/**
 * The add-on a modal variant buys. A Flex recharge buys the agent usage reset instead. Any other
 * variant with none still collects a card, but its confirm buys nothing yet.
 */
export function addonSku(key: AddonKey): AddonSku | undefined {
  switch (key) {
    case 'segments':
      return 'data_driven_segments';
    case 'activity-history':
      return 'data_retention';
    case 'flex-recharge':
    case 'flex-recharge-auto':
    case 'generic':
      return undefined;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

/**
 * The start of the at-cap sentence, for copy that ends it around its own links. It matches `limitRemedy` in services
 * `core/segments/limits.go`, so a banner reads the same as the refusal.
 */
export const SEGMENTS_ADDON_REMEDY = `Get unlimited data-driven segments with the segments add-on for ${formatCentsToUsd(ADDONS.segments.priceCents, 0)} per billing cycle`;

function afterLine(request: AddonRequest): AfterLineRun[] {
  switch (request.key) {
    case 'segments':
      return [
        { text: String(request.includedCount), isStrong: true },
        { text: ' included today ' },
        'arrow',
        { text: ' unlimited', isStrong: true },
        { text: ' data-driven segments' },
      ];
    case 'activity-history':
      return [
        { text: `${request.kind === 'message' ? 'Message' : 'Profile'} activity: see back to ` },
        { text: formatShortDate(request.lookbackStart), isStrong: true },
        { text: ' today ' },
        'arrow',
        { text: ' back to ' },
        { text: formatShortDate(request.addonLookbackStart), isStrong: true },
      ];
    case 'flex-recharge':
    case 'flex-recharge-auto':
      return [{ text: 'Brings your agent usage back to ' }, { text: '0%', isStrong: true }, { text: '.' }];
    case 'generic':
      return [
        { text: 'What this unlocks, stated as ' },
        { text: 'today', isStrong: true },
        { text: ' ' },
        'arrow',
        { text: ' ' },
        { text: 'after', isStrong: true },
      ];
    default: {
      const _exhaustive: never = request;
      return _exhaustive;
    }
  }
}

function addonTitle(request: AddonRequest): string {
  if (request.key !== 'activity-history') return ADDONS[request.key].title;
  return `${ACTIVITY_HISTORY_WINDOWS[request.kind].addonDays} days of ${request.kind} activity history`;
}

function addonPriceCents(request: AddonRequest): number {
  if (request.key === 'flex-recharge' || request.key === 'flex-recharge-auto') {
    return flexRechargeCents(request.usedPercent);
  }
  return ADDONS[request.key].priceCents;
}

export function addonOffer(request: AddonRequest): AddonOffer {
  return {
    ...ADDONS[request.key],
    title: addonTitle(request),
    priceCents: addonPriceCents(request),
    afterLine: afterLine(request),
  };
}

export function addonSubline(cadence: AddonCadence): string {
  const lead = cadence === 'monthly' ? 'An add-on billed to your card' : 'A one-time add-on billed to your card';
  return `${lead}, separate from your credit balance`;
}

export function addonPrice(offer: Pick<AddonConfig, 'cadence' | 'priceCents'>): string {
  return offer.cadence === 'monthly' ? formatCentsToUsd(offer.priceCents, 0) : formatCentsToUsd(offer.priceCents);
}

export function addonCadenceLabel(cadence: AddonCadence): string {
  return cadence === 'monthly' ? 'per month' : 'one time';
}

export function addonConfirmLabel(offer: Pick<AddonConfig, 'cadence' | 'priceCents'>, isAutoRecharge: boolean): string {
  if (isAutoRecharge) return 'Turn on auto-recharge';
  const price = addonPrice(offer);
  return offer.cadence === 'monthly' ? `Add for ${price}/month` : `Pay ${price}`;
}

export function prorationTodayLabel(proration: AddonProration): string {
  const range = `${formatShortDate(proration.today)} – ${formatShortDate(proration.nextBillingDate)}`;
  return `Today · ${range} (${proration.daysLeft} of ${proration.daysInCycle} days)`;
}

export type AutoRechargeSettings = {
  isEnabled: boolean;
  thresholdPercent: number | null;
  isMonthlyLimitEnabled: boolean;
  monthlyLimitDollars: number | null;
};

export type AutoRechargeErrors = { threshold?: string; monthlyLimit?: string };

export const DEFAULT_AUTO_RECHARGE_SETTINGS: AutoRechargeSettings = {
  isEnabled: false,
  thresholdPercent: 100,
  isMonthlyLimitEnabled: false,
  monthlyLimitDollars: null,
};

export function autoRechargeErrors(settings: AutoRechargeSettings, rechargeCents: number): AutoRechargeErrors {
  if (!settings.isEnabled) return {};

  const errors: AutoRechargeErrors = {};
  const { thresholdPercent, monthlyLimitDollars } = settings;
  if (
    thresholdPercent == null ||
    !Number.isInteger(thresholdPercent) ||
    thresholdPercent < 1 ||
    thresholdPercent > 100
  ) {
    errors.threshold = 'Enter a whole percent from 1 to 100.';
  }
  if (settings.isMonthlyLimitEnabled && (monthlyLimitDollars ?? 0) * 100 < rechargeCents) {
    errors.monthlyLimit = `Set the limit to at least ${formatCentsToUsd(rechargeCents, rechargeCents % 100 === 0 ? 0 : 2)}.`;
  }
  return errors;
}
